import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const useOrders = (statusId, title) => {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [shipType, setShipType] = useState('');
  const [filterSub, setFilterSub] = useState('');
  const [orderIdSubsidiary, setOrderIdSubsidiary] = useState(0);
  const [sortOrder, setSortOrder] = useState('id'); // Por defecto, se ordena por ID

  const [modals, setModals] = useState({
    subsidiary: false,
  });

  const navigate = useNavigate();

  const totalPages = Math.ceil(total / 25);
  const shippingTypes = useMemo(
    () => [
      ...new Set(orders.map(({ shipping_method }) => shipping_method)),
    ],
    [orders]
  );

  useEffect(() => {
    fetchTotalOrders();
    setCurrentPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusId]);

  useEffect(() => {
    fetchOrders(currentPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusId, sortOrder, shipType, filterSub]);

  const handleShipTypeChange = (type) => setShipType(type);
  const handleFilterSubChange = (sub) => setFilterSub(sub);

  const handleCurrentPage = (page) => setCurrentPage(page);

  const openOrderSubsidiaryModal = (orderId) => {
    setOrderIdSubsidiary(orderId);
    setModals((prevState) => ({ ...prevState, subsidiary: true }));
  }
  const closeOrderSubsidiaryModal = () =>{
    setOrderIdSubsidiary(0);
    setModals((prevState) => ({ ...prevState, subsidiary: false }));
  }

  const fetchTotalOrders = () => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_NODE_URL}/api/orders`, {
        params: { status_id: statusId, page: 1 },
        headers: {
          'ngrok-skip-browser-warning': true,
       
        },
      })
      .then((response) => {
        setTotal(response.data.total);
      })
      .catch((error) => {
        console.error('Error fetching total orders:', error);
      });
  };

  const fetchOrders = async (page) => {
    setLoading(true);
    try {
      // Obtener las órdenes desde el backend
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_NODE_URL}/api/orders`,
        {
          params: { status_id: statusId, page },
          headers: {
            'ngrok-skip-browser-warning': true,
         
          },
        }
      );

      const fetchedOrders = response.data.orders; // Órdenes obtenidas
      setOrders(fetchedOrders); // Actualizamos las órdenes inicialmente para renderizado rápido

      // Extraer los customer_id únicos, evitando duplicados y IDs inválidos (null o undefined)
      const customerIds = [
        ...new Set(
          fetchedOrders
            .map((order) => order.customer_id)
            .filter((id) => id)
        ),
      ];

      if (customerIds.length > 0) {
        // Llamar al endpoint para obtener datos de los clientes por lote
        const customersResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_NODE_URL}/api/customers`,
          {
            params: { 'id:in': customerIds.join(',') },
            headers: {
              'ngrok-skip-browser-warning': true,
           
            },
          }
        );

        const customersData = customersResponse.data.data; // Datos de los clientes desde la API

        // Crear un mapa para acceder a los datos de los clientes rápidamente
        const customersMap = customersData.reduce((map, customer) => {
          map[customer.id] = customer.first_name; // Asociamos el ID del cliente con su primer nombre
          return map;
        }, {});

        // Actualizar las órdenes con el nombre del cliente obtenido
        setOrders((prevOrders) =>
          prevOrders.map((order) => ({
            ...order,
            customer_first_name:
              customersMap[order.customer_id] || 'Desconocido', // Añadimos el nombre del cliente o 'Desconocido' si no se encuentra
          }))
        );
      }

      // Opcional: Actualizar también los métodos de envío para cada orden
      fetchedOrders.forEach(async (order) => {
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            o.id === order.id
              ? {
                ...o,
                shipping_method:
                  order.consignments.shipping[0]
                    .shipping_method,
              }
              : o
          )
        );
      });

      if (shipType !== '') {
        setOrders((prevState) =>
          !prevState.filter(
            ({ shipping_method }) => shipping_method === shipType
          ).length
            ? prevState
            : prevState.filter(
              ({ shipping_method }) =>
                shipping_method === shipType
            )
        );
      }

      if (filterSub !== '') {
        setOrders((prevState) =>
          prevState.filter(({ subsidiary, consignments }) => {
            const shippingMethod = consignments?.shipping[0]?.shipping_method || '';
            const hasSubsidiarydInMethod = shippingMethod.includes('Sucursal');
            if (filterSub === 'N/A') {
              return !subsidiary && hasSubsidiarydInMethod;
            }
            if (filterSub === 'Unassigned') {
              return !subsidiary && !hasSubsidiarydInMethod;
            }
            return subsidiary?.id === filterSub;
          })
        );
      }

      setLoading(false); // Marcamos como terminado el proceso de carga
    } catch (error) {
      console.error('Error fetching orders or customers:', error);
      setLoading(false); // Nos aseguramos de detener la animación de carga incluso en caso de error
    }
  };

  const fetchShippingMethod = async (orderId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_NODE_URL}/api/orders/${orderId}/consignments`,
        {
          headers: {
            'ngrok-skip-browser-warning': true,
         
          },
        }
      );
      if (response.data.shipping && response.data.shipping.length > 0) {
        return response.data.shipping[0].shipping_method;
      }
    } catch (error) {
      console.error('Error fetching shipping method:', error);
    }
    return 'No disponible';
  };

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`, {
      state: { from: title.toLowerCase(), sortOrder },
    }); // Pasar sortOrder al cambiar de vista
  };

  const formatCurrency = (amount) => `$${parseFloat(amount).toFixed(2)}`;

  const extractScheduledDate = (message) => {
    if (!message) {
      return null;
    }
    const match = message.match(
      /Fecha programada:\s*(\d{2}\/\d{2}\/\d{4})/
    );
    return match ? match[1] : null;
  };

  const isToday = (dateString) => {
    if (!dateString) return false; // Si no hay fecha, devolvemos false directamente
    const today = new Date();
    const [day, month, year] = dateString.split('/');
    return (
      today.getDate() === parseInt(day, 10) &&
      today.getMonth() + 1 === parseInt(month, 10) &&
      today.getFullYear() === parseInt(year, 10)
    );
  };

  const handleStatusChange = (orderId, newStatusId) => {
    axios
      .put(
        `${process.env.REACT_APP_BACKEND_NODE_URL}/api/orders/${orderId}`,
        {
          status_id: newStatusId,
        }
      )
      .then(() => {
        fetchOrders(currentPage); // Refresh orders list after status update
      })
      .catch((error) => {
        console.error('Error updating order status:', error);
      });
  };

  const getStatusOptions = (order) => {
    const currentStatus = {
      value: order.status_id,
      label: order.custom_status || order.status,
    };
    let nextStatusOptions = [];

    switch (statusId) {
      case 7: // Orden Creada
        nextStatusOptions = [{ value: 1, label: 'En Proceso' }];
        break;
      case 1: // En Proceso
        nextStatusOptions = [{ value: 9, label: 'Preparando pedido' }];
        break;
      case 9: // Preparando envío
        if (order.payment_method === 'Pagar a la entrega') {
          nextStatusOptions = [
            { value: 8, label: 'Listo y en espera de recolección' },
          ];
        } else if (order.payment_method === 'Tarjeta de Crédito') {
          nextStatusOptions = [
            {
              value: 8,
              label: 'Pagado y en espera de recolección',
            },
            { value: 11, label: 'Pagado y en proceso de envío' },
          ];
        }
        break;
      case 8: // Pagado y en espera de recolección
      case 12: // Listo y en espera de recolección
        nextStatusOptions = [{ value: 10, label: 'Entregado' }];
        break;
      case 11: // Pagado y en proceso de envío
        nextStatusOptions = [{ value: 2, label: 'En Ruta' }];
        break;
      case 2: // En Ruta
        nextStatusOptions = [{ value: 10, label: 'Entregado' }];
        break;
      default:
        break;
    }

    // Devuelve el estado actual y las opciones futuras permitidas
    return [currentStatus, ...nextStatusOptions];
  };

  const handleRefresh = () => {
    setUpdating(true);
    fetchTotalOrders();
    fetchOrders(currentPage).then(() => setUpdating(false));
  };

  const handleSortById = () => {
    setSortOrder('id');
  };

  const handleSortByScheduledDate = () => {
    setSortOrder('scheduled_date');
  };

  return {
    currentPage,
    filterSub,
    loading,
    orders,
    modals,
    shipType,
    sortOrder,
    total,
    totalPages,
    updating,
    shippingTypes,
    orderIdSubsidiary,

    //Methods
    openOrderSubsidiaryModal,
    closeOrderSubsidiaryModal,
    fetchShippingMethod,
    extractScheduledDate,
    formatCurrency,
    getStatusOptions,
    handleCurrentPage,
    handleOrderClick,
    handleRefresh,
    handleShipTypeChange,
    handleFilterSubChange,
    handleSortById,
    handleSortByScheduledDate,
    handleStatusChange,
    isToday,
  };
};

export default useOrders;